import { useQuery } from "@tanstack/react-query"
import {
  Activity,
  ArrowUpRight,
  BookText,
  Building2,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  ScanSearch,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from "lucide-react"
import { type FormEvent, useState } from "react"
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom"
import { ApiError, api } from "@/api.js"
import { Button } from "@/components/ui/button.js"
import { cn } from "@/lib/utils.js"
import { readLookup } from "@/lookup.js"
import { AccessScreen } from "./screens/Access.js"
import { ApiClientsScreen } from "./screens/ApiClients.js"
import { CheckTaxIdScreen } from "./screens/CheckTaxId.js"
import { ConfigurationScreen } from "./screens/Configuration.js"
import { DashboardScreen } from "./screens/Dashboard.js"
import { DecisionDetailScreen } from "./screens/DecisionDetail.js"
import { DecisionsScreen } from "./screens/Decisions.js"
import { EntitiesScreen } from "./screens/Entities.js"
import { EntityScreen } from "./screens/Entity.js"
import { HealthScreen } from "./screens/Health.js"
import { OverridesScreen } from "./screens/Overrides.js"
import { SettingsScreen } from "./screens/Settings.js"

/**
 * Navigation grouped by intent — the groups mirror who opens the panel:
 * reading what happened, steering the system, asking it something.
 */
const NAV: readonly {
  label: string
  items: readonly {
    to: string
    label: string
    icon: typeof ListChecks
    /** Outside the SPA: a plain link, opened in a tab of its own. */
    external?: boolean
  }[]
}[] = [
  {
    label: "Przegląd",
    items: [
      { to: "/dashboard", label: "Pulpit", icon: LayoutDashboard },
      { to: "/decisions", label: "Decyzje", icon: ListChecks },
      { to: "/entities", label: "Podmioty", icon: Building2 },
      { to: "/health", label: "Stan systemu", icon: Activity },
    ],
  },
  {
    label: "Sterowanie",
    items: [
      { to: "/overrides", label: "Odstępstwa", icon: ShieldCheck },
      { to: "/config", label: "Konfiguracja", icon: SlidersHorizontal },
      { to: "/api-clients", label: "Klucze API", icon: KeyRound },
      { to: "/access", label: "Dostęp do panelu", icon: Users },
      { to: "/settings", label: "Ustawienia", icon: Settings2 },
    ],
  },
  {
    label: "Narzędzia",
    items: [
      { to: "/check", label: "Sprawdź NIP", icon: ScanSearch },
      /*
       * Swagger UI over the same document `/v1/openapi.json` serves — the
       * contract a consumer integrates against, one click from the panel
       * rather than a path somebody has to be told. Same origin in production
       * and proxied in dev, so the relative href holds in both; it is a page
       * outside the SPA, so it is a link and not a route.
       */
      { to: "/v1/docs", label: "Dokumentacja API", icon: BookText, external: true },
    ],
  },
]

const NAV_ITEM =
  "relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13.5px] no-underline transition-colors"
const NAV_ITEM_IDLE = "text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
const NAV_ITEM_ACTIVE =
  "bg-sidebar-accent font-medium text-sidebar-foreground before:absolute before:left-0 before:h-4 before:w-0.5 before:rounded-full before:bg-iris [&_svg]:text-iris"

export function App() {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    retry: false,
  })

  if (session.isPending) {
    return <p className="py-24 text-center text-muted-foreground">Wczytywanie…</p>
  }

  // Google Workspace SSO. The panel never handles a password; an
  // unauthenticated session is bounced to the identity provider by the API.
  if (session.error instanceof ApiError && session.error.status === 401) return <SignIn />

  return (
    <div className="flex min-h-screen">
      {/*
       * The chrome: identity, lookup and navigation on near-black, so the
       * white surface next to it reads as the page. The eye sits on the
       * ground it was drawn for.
       */}
      <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col bg-sidebar px-4 py-5 text-sidebar-foreground">
        <div className="flex items-center gap-3 px-1">
          <span className="eye-glow relative grid size-9 shrink-0 place-items-center">
            <img src="/logo.png" alt="" className="size-8" />
          </span>
          <div className="leading-tight">
            <p className="text-[13px] font-semibold tracking-[0.2em]">SAURON</p>
            <p className="text-xs text-sidebar-muted">Powered by Packhelp</p>
          </div>
        </div>

        <EntityLookup />

        <nav className="mt-2 flex flex-col">
          {NAV.map((group) => (
            <div key={group.label} className="mt-5">
              <p className="px-2.5 pb-1.5 text-[10.5px] font-medium uppercase tracking-[0.1em] text-sidebar-faint">
                {group.label}
              </p>
              <div className="flex flex-col gap-px">
                {group.items.map(({ to, label, icon: Icon, external }) =>
                  external === true ? (
                    <a
                      key={to}
                      href={to}
                      target="_blank"
                      rel="noreferrer"
                      className={cn(NAV_ITEM, NAV_ITEM_IDLE)}
                    >
                      <Icon className="size-4 shrink-0 opacity-90" />
                      {label}
                      {/* The glyph is what says this one leaves the panel. */}
                      <ArrowUpRight className="ml-auto size-3.5 shrink-0 opacity-70" />
                    </a>
                  ) : (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        cn(NAV_ITEM, isActive ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE)
                      }
                    >
                      <Icon className="size-4 shrink-0 opacity-90" />
                      {label}
                    </NavLink>
                  ),
                )}
              </div>
            </div>
          ))}
        </nav>

        <div className="mt-auto flex items-center justify-between gap-2 px-1 pt-4">
          <p className="truncate text-xs text-sidebar-faint">{session.data?.email}</p>
          <button
            type="button"
            onClick={() => {
              // A hard reload after logout is the simplest way back to a clean
              // auth state: the session query refetches, 401s, and the panel
              // shows the sign-in screen. The reload runs even if the POST fails.
              void api
                .logout()
                .catch(() => undefined)
                .finally(() => window.location.assign("/"))
            }}
            className="shrink-0 text-xs text-sidebar-faint underline-offset-2 transition-colors hover:text-sidebar-foreground hover:underline"
          >
            Wyloguj
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="max-w-[1120px] px-10 py-8">
          <Routes>
            {/* The front page is the Pulpit: what the gate has been answering, and why. */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardScreen />} />
            <Route path="/decisions" element={<DecisionsScreen />} />
            <Route path="/decisions/:decisionId" element={<DecisionDetailScreen />} />
            <Route path="/entities" element={<EntitiesScreen />} />
            <Route path="/entities/:country/:taxId" element={<EntityScreen />} />
            <Route path="/health" element={<HealthScreen />} />
            <Route path="/overrides" element={<OverridesScreen />} />
            <Route path="/config" element={<ConfigurationScreen />} />
            <Route path="/api-clients" element={<ApiClientsScreen />} />
            <Route path="/access" element={<AccessScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="/check" element={<CheckTaxIdScreen />} />
          </Routes>
        </div>
      </main>
    </div>
  )
}

/**
 * One box, two answers. A complete identifier goes straight to the entity
 * screen, which is the workflow support arrives with and which works for a
 * taxpayer nobody has ever asked about; anything else — a fragment, a REGON, a
 * KRS number, a name — is a question only the list can answer, so it goes to
 * Podmioty as a filter.
 *
 * **Searching by name was added on 2026-09-08, by the product owner, knowing
 * what it is.** The registered name is a sole trader's own name for 57% of the
 * base, so an index over it is the step CLAUDE.md's *The thing everyone will
 * ask for* had left to counsel. What it is not is a widening of what is
 * stored: the fields matched are ones `gus_profile` already held, and the
 * address, telephone number and email address beside them are still not
 * searched. The placeholder is what says which fields those are.
 */
function EntityLookup() {
  const [value, setValue] = useState("")
  const navigate = useNavigate()

  function submit(event: FormEvent) {
    event.preventDefault()
    const lookup = readLookup(value)
    if (lookup === null) return
    setValue("")
    void navigate(
      lookup.kind === "entity"
        ? `/entities/${lookup.target.country}/${lookup.target.taxId}`
        : `/entities?q=${encodeURIComponent(lookup.text)}`,
    )
  }

  return (
    <form onSubmit={submit} className="mt-5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-sidebar-faint" />
        <input
          className="h-8 w-full rounded-md border border-sidebar-border bg-white/5 pl-8 pr-2.5 text-[13px] text-sidebar-foreground transition-colors placeholder:text-sidebar-faint focus:border-iris/70 focus:outline-none"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="NIP, REGON, KRS lub nazwa…"
          aria-label="Szukaj podmiotu"
        />
      </div>
    </form>
  )
}

function SignIn() {
  // The API bounces a failed or refused login back here with a reason, so the
  // sign-in screen can say what went wrong rather than looping silently. A
  // refusal is two words on purpose (product owner, 2026-09-15): whoever is
  // reading them is not on the list, and which rule kept them out is not theirs
  // to learn.
  const problem = new URLSearchParams(window.location.search).get("sso")
  const message =
    problem === "denied"
      ? "Brak dostępu."
      : problem === "error"
        ? "Logowanie nie powiodło się. Spróbuj ponownie."
        : null

  return (
    <main className="grid min-h-screen place-items-center bg-sidebar text-sidebar-foreground">
      <div className="px-6 pb-16 text-center">
        <span className="eye-glow mx-auto mb-7 grid size-28 place-items-center">
          <img src="/logo.png" alt="" className="size-20" />
        </span>
        <h1 className="text-lg font-semibold tracking-[0.25em]">SAURON</h1>
        <p className="mt-2 mb-8 text-sm text-sidebar-muted">
          Panel wewnętrzny · logowanie przez Google Workspace
        </p>
        {message !== null && <p className="mx-auto mb-6 max-w-xs text-sm text-iris">{message}</p>}
        <Button asChild>
          <a href="/v1/admin/auth/google">Zaloguj przez Google</a>
        </Button>
      </div>
    </main>
  )
}
